/* ──────────────────────────────────────────────────────────────
   src/pages/reports/profesores/TotalesProfesorPage.tsx
   Totales de profesores – barra-chart + lista agrupada por centros
   ────────────────────────────────────────────────────────────── */
import { useTranslation } from "react-i18next";
import { useState } from "react";
import {
  Stack,
  Button,
  LinearProgress,
  Alert,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListSubheader, // 🆕
} from "@mui/material";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, Cell } from "recharts";

import { useTotalesProfesorReport } from "../../../hooks/useReportJob";

/* paleta rotatoria para las barras */
const COLORS = [
  "#4caf50",
  "#2196f3",
  "#ff9800",
  "#9c27b0",
  "#f44336",
  "#00bcd4",
  "#8bc34a",
  "#ff5722",
];

/* —— estructura exacta que devuelve el worker —— */
interface TotProfesorRow {
  tipo: string;
  n: number;
}
interface TotProfesorResult {
  total: number;
  rows: TotProfesorRow[];
  detalle: Record<string, Record<string, string[]>>; // tipo → centro → [profes]
}

export default function TotalesProfesorPage() {
  const { t } = useTranslation<"translation">();
  const { run, useStatus } = useTotalesProfesorReport();

  const [jobId, setJobId] = useState<string>();
  const [selTipo, setSelTipo] = useState<string | null>(null); // barra elegida

  /* estado del worker */
  const { data: status } = useStatus(jobId);

  /* lanzar / relanzar informe */
  const lanzar = async () => {
    const { jobId } = await run(); // ← el job no usa parámetros
    setSelTipo(null); // limpia selección
    setJobId(jobId);
  };

  /* dataset para el gráfico ------------------------------------ */
  const result = status?.result as TotProfesorResult | undefined;

  // evitamos duplicar “Total” (puede venir ya en rows)
  const rowsSinTotal = result?.rows.filter((r) => r.tipo !== "Total") ?? [];

  const chartData: TotProfesorRow[] = result
    ? [{ tipo: "Total", n: result.total }, ...rowsSinTotal]
    : [];

  /* click en una barra */
  const handleBarClick = (data: any) => {
    const tipo = (data.payload as TotProfesorRow).tipo;
    setSelTipo((prev) => (prev === tipo ? null : tipo));
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h5">{t("totalesProfesor.titulo")}</Typography>

      <Button variant="contained" onClick={lanzar}>
        {t("totalesProfesor.estado.generando")}
      </Button>

      {/* progreso */}
      {status && !status.done && (
        <Stack spacing={1}>
          <LinearProgress variant="determinate" value={status.progress} />
          <Typography variant="body2">
            {t("totalesProfesor.estado.generando", {
              progress: status.progress,
            })}
          </Typography>
        </Stack>
      )}

      {/* resultado */}
      {status?.done && result && (
        <>
          <Alert severity="success">
            {t("totalesProfesor.estado.exito", { count: chartData.length })}
          </Alert>

          <Paper sx={{ p: 2 }}>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={chartData} margin={{ top: 20, right: 30, left: 10, bottom: 5 }}>
                <XAxis dataKey="tipo" />
                <YAxis allowDecimals={false} />
                <Tooltip formatter={(v: number) => [v, t("totalesProfesor.chart.leyenda")]} />
                <Legend />
                <Bar
                  dataKey="n"
                  name={t<string>("totalesProfesor.chart.leyenda")}
                  onClick={handleBarClick}
                >
                  {chartData.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} cursor="pointer" />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          {/* lista de profesores del tipo seleccionado */}
          {selTipo && (
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t("totalesProfesor.lista.titulo", { tipo: selTipo })}
              </Typography>

              <List subheader={<li />}>
                {Object.entries(result.detalle[selTipo] ?? {}).map(([centro, profes]) => (
                  <li key={centro}>
                    <ul style={{ padding: 0 }}>
                      <ListSubheader disableSticky>{centro}</ListSubheader>
                      {profes.map((nombre) => (
                        <ListItem key={nombre} sx={{ pl: 4 }}>
                          <ListItemText primary={nombre} />
                        </ListItem>
                      ))}
                    </ul>
                  </li>
                ))}
              </List>
            </Paper>
          )}
        </>
      )}
    </Stack>
  );
}
